<template>
  <v-container fluid class="max-width-container pa-0 py-6">
    <v-row justify="center" class="gap-y-6">

      <!-- LEFT COLUMN: Selected Equipment & Quote Summary -->
      <v-col cols="12" md="5">
        <CheckoutSummary
          :cart-total-monthly-price="cartTotalMonthlyPrice || 0"
          @remove-item="$emit('remove-item', $event)"
        />
      </v-col>

      <!-- RIGHT COLUMN: Installation Address Form -->
      <v-col cols="12" md="7">
        <v-card class="pa-8 border-card shadow-summary h-100" rounded="xl" style="background: white;">
          <!-- Header and Title (Hidden during loading) -->
          <template v-if="!preapprovalLoading">
            <div class="text-left mb-6">
              <h2 class="text-h5 font-weight-black text-slate-800 mt-0 mb-2">Where will the machine be installed</h2>
              <p class="text-body-2 text-slate-500">We use this to confirm local service coverage and delivery.</p>
            </div>
          </template>

          <!-- Submission Loading State -->
          <div v-if="preapprovalLoading" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-6" />
            <h3 class="text-h5 font-weight-black text-blue-grey-darken-4 mb-2">
              Submitting quote request
            </h3>
            <p class="text-body-2 text-grey-darken-1">
              Finalizing your configuration details...
            </p>
          </div>

          <v-form v-else ref="address_form" v-model="addressFormValid" @submit.prevent="SubmitAddress">
            <!-- Error Alert -->
            <v-alert v-if="preapprovalError" type="error" variant="tonal" class="mb-6 text-left" rounded="lg">
              {{ preapprovalError }}
            </v-alert>
            <v-row class="gap-y-4">
              <v-col cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">STREET ADDRESS</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.required]"
                  v-model="form.address.street"
                  placeholder="e.g. 4210 W Kennedy Blvd">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">SUITE OR FLOOR, OPTIONAL</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  v-model="form.address.suite"
                  placeholder="e.g. Suite 200">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">CITY</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  placeholder="e.g. Tampa"
                  v-model="form.address.city"
                  :rules="[rules.required]">
                </v-text-field>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">STATE</span>
                <v-combobox
                  hide-details
                  rounded="lg"
                  :items="states"
                  color="primary"
                  variant="outlined"
                  @blur="OnStateBlur"
                  placeholder="e.g. FL"
                  density="comfortable"
                  :rules="[rules.required]"
                  v-model="form.address.state"
                  @update:model-value="FormatStateAbbreviation">
                </v-combobox>
              </v-col>

              <v-col
                sm="6"
                cols="12">
                <span class="input-label font-weight-black text-slate-500 d-block mb-1">ZIP CODE</span>
                <v-text-field
                  rounded="lg"
                  hide-details
                  color="primary"
                  variant="outlined"
                  density="comfortable"
                  placeholder="e.g. 33609"
                  v-model="form.address.zip"
                  :rules="[rules.required]">
                </v-text-field>
              </v-col>
            </v-row>

            <v-btn
              type="submit"
              :color="ConfigElementsColor"
              block
              rounded="lg"
              size="large"
              elevation="0"
              class="text-none font-weight-bold cta-btn mt-8 mb-2"
            >
              Request pricing quote
            </v-btn>

            <p class="dealer-acknowledgment text-center mb-3">
              By submitting this request, you acknowledge that 1-800 Office Solutions may share your information with one independent dealer from its selected network to respond to your inquiry. Participating dealers may pay 1-800 Office Solutions for access to these opportunities. See our <a href="https://1800officesolutions.com/terms-and-conditions/" target="_blank" rel="noopener">Terms & Conditions</a> and <a href="https://1800officesolutions.com/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>.
            </p>

            <v-btn
              variant="outlined"
              color="primary"
              block
              rounded="lg"
              size="large"
              class="text-none font-weight-bold"
              @click="configurator_step = 'contact'"
            >
              Back to contact details
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import CheckoutSummary from '@/components/configurator/CheckoutSummary.vue'

defineProps<{
  copier?: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  cartTotalMonthlyPrice?: number
  preapprovalLoading?: boolean
  preapprovalError?: string
}>()

const emit = defineEmits<{
  (e: 'remove-item', idx: number): void
  (e: 'submit'): void
}>()

const store = useGlobalsStore()
const { form, states, configurator_step, ConfigElementsColor } = storeToRefs(store)

const address_form = ref<any>(null)
const addressFormValid = ref(false)

const rules = {
  required: (v: any) => !!v || 'Field is required'
}

const us_state_name_to_abbr: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'virgin islands': 'VI',
  'puerto rico': 'PR',
  guam: 'GU'
}

function GetStateAbbreviation(val: any): string {
  if (!val) return ''
  const raw_val = typeof val === 'object' && val !== null ? (val.value ?? val.title ?? '') : val
  const trimmed = String(raw_val).trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()

  if (us_state_name_to_abbr[lower]) {
    return us_state_name_to_abbr[lower]
  }
  if (trimmed.length <= 2) {
    return trimmed.toUpperCase()
  }
  const found = Object.entries(us_state_name_to_abbr).find(([name]) => lower.startsWith(name) || name.startsWith(lower))
  if (found) {
    return found[1]
  }
  return trimmed.slice(0, 2).toUpperCase()
}

function FormatStateAbbreviation(val?: any) {
  const target_val = val !== undefined ? val : form.value.address.state
  const formatted = GetStateAbbreviation(target_val)
  form.value.address.state = formatted
}

function OnStateBlur(event?: any) {
  const input_val = event?.target?.value ?? form.value.address.state
  if (input_val) {
    FormatStateAbbreviation(input_val)
  }
}

watch(
  () => form.value.address.state,
  (new_val) => {
    if (typeof new_val === 'string' && new_val) {
      const formatted = GetStateAbbreviation(new_val)
      if (formatted && formatted !== new_val) {
        form.value.address.state = formatted
      }
    }
  }
)

function SubmitAddress() {
  if (form.value.address.state) {
    FormatStateAbbreviation(form.value.address.state)
  }
  if (address_form.value?.validate) {
    address_form.value.validate().then((res: any) => {
      if (res.valid) {
        emit('submit')
      }
    })
  } else if (form.value.address.street && form.value.address.zip && form.value.address.city && form.value.address.state) {
    emit('submit')
  }
}
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1400px !important;
  margin: 0 auto !important;
  width: 100% !important;
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.shadow-summary {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}
.border-bottom-thin {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.notice-banner {
  background-color: #f0fdf4;
}
.price-val {
  letter-spacing: -1px;
  font-size: 38px;
}
.cta-btn {
  font-size: 1.1rem !important;
  height: 52px !important;
}
.text-slate-800 {
  color: #1e293b !important;
}
.text-slate-500 {
  color: #64748b !important;
}
.text-slate-700 {
  color: #334155 !important;
}
.text-slate-400 {
  color: #94a3b8 !important;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dealer-acknowledgment {
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
  margin-top: 8px;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
}
</style>
